import { saveAs } from 'file-saver';

import {
  FIOPattern,
  EmailPattern,
  UrlPattern,
  EAccountUrlBeginnings,
  NicknamePattern,
  RussianRegExp,
  UkraineLetters,
} from './constants';
import { IS_SERVER } from './envs';

export const validateEmail = (email: string): boolean =>
  EmailPattern.test(email);

export const validateUrl = (url: string): boolean => UrlPattern.test(url);

export const validatePhoneNumber = (phoneNumber: string): boolean =>
  /(\+7)\s\(9\d{2}\)\s(\d{3})-(\d{2})-(\d{2})/.test(phoneNumber) ||
  (phoneNumber.includes('+7') && /\+\d{11}/.test(phoneNumber)) ||
  /(\+380)\s\(\d{2}\)\s(\d{3})-(\d{2})-(\d{2})/.test(phoneNumber) ||
  (phoneNumber.includes('+380') && /\+\d{12}/.test(phoneNumber));

export const validateSpaces = (value: string): boolean =>
  /^(?:[\S]*)$/.test(value) || value.trim().length > 0;

// валидация полей с именем/фамилией/отчеством
export const validateFIO = (value: string): boolean =>
  FIOPattern.test(value) && !UkraineLetters.test(value);

// валидация полей с никнеймом
export const validateNickname = (value: string): boolean =>
  NicknamePattern.test(value);

// валидация русского текста
export const validateText = (value: string): boolean =>
  RussianRegExp.test(value);

// валидация для полей соц сетей
export const validateSocialUrl = (url: string): boolean => {
  if (!url) return true;
  return validateUrl(url) || url.startsWith('@');
};

// https://proweb63.ru/help/js/declension-in-js
export const decl = (number: number, txt: string[]) => {
  const cases = [2, 0, 1, 1, 1, 2];

  return txt[
    number % 100 > 4 && number % 100 < 20
      ? 2
      : cases[number % 10 < 5 ? number % 10 : 5]
  ];
};

// замена символов переноса строк на определенный символ
export const getFormattedText = (str: string, char: string) =>
  str.replace(/(?:\r\n|\r|\n)/g, char);

// https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
export const formatBytes = (bytes: number, decimals: number = 2) => {
  if (bytes === 0) return '0 Байт';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Байт', 'КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

//сортировка массива в рандомном порядке и обрезка его до указанной длины
export const shuffleAndSliceArray = <T>(
  items: Array<T>,
  slicedCount?: number
): Array<T> =>
  [...items]
    .sort(() => Math.random() - 0.5)
    .slice(0, slicedCount ?? items.length);

//сохранят файл в файловую систему пользователя
export const saveFile = (url: string | Blob, fileName: string = '') => {
  saveAs(url, fileName);
};

// преобразует данные в pdf формат
export const convertToPDFAndSave = (data: any) => {
  const blob = new Blob([data], {
    type: 'application/pdf',
  });
  const url = window.URL.createObjectURL(blob);

  window.open(url);

  saveFile(blob);
};

export const isFileSizeExceedMaxSize = (
  size: number,
  maxSize?: number
): boolean => {
  const BYTES_PER_MEGA_BYTES = 1e6;

  if (!maxSize) {
    return false;
  }
  const convertedMaxSize = maxSize * BYTES_PER_MEGA_BYTES;

  return size > convertedMaxSize;
};

export const upperCaseFirst = (value: string): string => {
  return value.charAt(0).toUpperCase() + value.substr(1);
};

export const snakeToCamelCase = (str: string): string =>
  str
    .toLowerCase()
    .replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('_', ''));

export const getCurrentLink = (): string =>
  !IS_SERVER ? window.location.href : '';

// рандомное число от 0 до max
export const getRandomValueFromZeroToMax = (max: number) =>
  Math.floor(Math.random() * max);

// рандомное число от min до max
export const getRandomValueFromMinToMax = (min: number, max: number) =>
  Math.round(min - 0.5 + Math.random() * (max - min + 1));

// преобразовывает ссылки к соответствующему формату
// www.t.me/username => https://www.t.me/username
// t.me/username => https://t.me/username
// @username => https://t.me/username
// vk.com/faq18366 => https://vk.com/faq18366
// www.vk.com/faq18366 => https://www.vk.com/faq18366

export const formatUrl = (url: string) => {
  const httpsRegExp = /https:\/\//;
  const tgRegExp = /t\.me\/|@/;
  const vkRegExp = /vk/;

  let formattedUrl = url;

  if (!httpsRegExp.test(url)) {
    if (vkRegExp.test(url)) {
      formattedUrl = `${EAccountUrlBeginnings.HTTPS}${formattedUrl}`;
    } else {
      if (tgRegExp.test(url)) {
        const matched = url.match(tgRegExp);

        if (matched) {
          if (matched[0] === EAccountUrlBeginnings.TG) {
            formattedUrl = `${EAccountUrlBeginnings.HTTPS}${formattedUrl}`;
          }

          if (matched[0] === EAccountUrlBeginnings.AT) {
            formattedUrl = `${EAccountUrlBeginnings.HTTPS}${
              EAccountUrlBeginnings.TG
            }${formattedUrl.replace(/^@/, '')}`;
          }
        }
      }
    }
  }

  return formattedUrl;
};

export const numToUiFormatter = (num: number) => {
  if (num >= 1000000) {
    return `${Math.floor(num / 1000000)}М`;
  } else if (num >= 1000) {
    return `${Math.floor(num / 1000)}К`;
  } else {
    return `${num}`;
  }
};

export const numberWithSeparator = (value: number, separator: string = ' ') => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
};

export const transformRatingPipe = (value: number) => {
  switch (value) {
    case 1:
      return 6;
    case 2:
      return 7;
    case 3:
      return 8;
    case 4:
      return 9;
    case 5:
      return 10;
  }
};

export const buildProfileUrl = () => {
  const env = process.env.NEXT_PUBLIC_WERF_ENV;
  let host = 'https://lk-dev.znanierussia.ru';

  if (env === 'production') {
    host = `https://lk.znanierussia.ru`;
  } else if (env !== 'dev_local') {
    host = `https://lk-${env}.znanierussia.ru`;
  }

  return host;
};

// remove any non-digits from the number
export const sanitizeNumber = (value: string): string =>
  value.replace(/\D/g, '');
